import type { ServiceDto } from '@fos/shared/contracts/services';
import { SERVICE_KIND_LABELS, SERVICE_STATUS_LABELS, SERVICE_STATUS_TONES } from '@fos/shared/labels';
import { Wrench } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { cn } from '../../../lib/cn';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { EmptyState, ErrorState, Skeleton } from '../../../ui/States';
import { StatusPill } from '../../../ui/StatusPill';
import { servicePriceText } from '../service-format';
import { ServiceFormDialog } from './ServiceFormDialog';

/**
 * Wiersze usług klienta w profilu klienta (Figma 05 Profil REA, sekcja „Projekty i usługi”):
 * „Utrzymanie strony · usługa · 490 zł / mies. · Aktywna”, wiersz 48 px z linią pod spodem.
 * Klik: karta zasobu objętego umową (Utrzymanie) albo formularz usługi (gdy osoba może edytować).
 * Nagłówek sekcji i wiersze projektów dokłada profil klienta (`ClientProjectsRows` z modułu Projekty).
 * Bez modułu Projekty ani Utrzymanie komponent nic nie pokazuje; w trybie częściowym klienta serwer nie zwraca usług.
 */
export function ClientServicesRows({
  clientId,
  showEmpty = false,
  includeArchived = false,
  className,
}: {
  clientId: string;
  /** pusty stan „Brak usług” (domyślnie wyłączony: sekcja ma też projekty) */
  showEmpty?: boolean;
  includeArchived?: boolean;
  className?: string;
}) {
  const access = useAccess();
  const enabled = access.can('projects') || access.can('maintenance');
  const query = trpc.services.listByClient.useQuery({ clientId, includeArchived }, { enabled });
  const [editing, setEditing] = useState<ServiceDto | null>(null);

  if (!enabled) return null;
  if (query.isPending) {
    return (
      <div role="status" aria-label="Wczytywanie usług" className={className}>
        <div className="flex h-12 items-center gap-3 border-b border-line">
          <Skeleton className="h-3.5 w-2/5" />
          <span className="flex-1" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
    );
  }
  if (query.isError) {
    return (
      <ErrorState
        compact
        title="Nie udało się wczytać usług"
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
        className={className}
      />
    );
  }
  if (query.data.length === 0) {
    return showEmpty ? <EmptyState compact icon={Wrench} title="Brak usług" className={className} /> : null;
  }

  const canEdit = (s: ServiceDto) => access.can(s.kind === 'maintenance' ? 'maintenance' : 'projects', 'edit');

  return (
    <>
      <ul className={className} aria-label="Usługi klienta">
        {query.data.map((s) => {
          const price = servicePriceText(s);
          const asset = s.kind === 'maintenance' && access.can('maintenance') ? s.assets[0] : undefined;
          const content = (
            <>
              <span className="min-w-0 truncate text-body-s font-medium text-fg">{s.name}</span>
              <span className="shrink-0 text-caption text-fg-muted">
                {s.kind === 'maintenance' ? 'usługa' : SERVICE_KIND_LABELS[s.kind].toLowerCase()}
              </span>
              <span className="flex-1" />
              {price && (
                <span className="hidden shrink-0 text-caption whitespace-nowrap text-fg-secondary sm:inline">
                  {price}
                </span>
              )}
              <StatusPill tone={SERVICE_STATUS_TONES[s.status]} className="shrink-0">
                {SERVICE_STATUS_LABELS[s.status]}
              </StatusPill>
            </>
          );
          const rowClass = cn(
            'flex min-h-12 w-full items-center gap-3 border-b border-line py-2 text-left',
            'transition-colors hover:bg-surface focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
          );
          return (
            <li key={s.id}>
              {asset ? (
                <Link to={`/utrzymanie/${asset.id}`} className={rowClass}>
                  {content}
                </Link>
              ) : canEdit(s) ? (
                <button type="button" className={rowClass} onClick={() => setEditing(s)}>
                  {content}
                </button>
              ) : (
                <div className={cn(rowClass, 'hover:bg-transparent')}>{content}</div>
              )}
            </li>
          );
        })}
      </ul>
      {editing && (
        <ServiceFormDialog
          open={Boolean(editing)}
          onOpenChange={(open) => !open && setEditing(null)}
          clientId={clientId}
          service={editing}
        />
      )}
    </>
  );
}
